import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import React, { forwardRef, useRef, useContext, useCallback } from 'react';
import { Check } from 'react-feather';
import { useUncontrolled } from '../../hooks/useUncontrolled.js';
import { CheckboxGroupContext } from './context/CheckboxGroup.context.js';
import defaultStyles from './styles/Checkbox.module.scss.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { isFunction } from '../../utils/is.js';
import { Typography } from '../typography/Typography.js';
import { omit } from '../../utils/omit.js';

const ID_SUFFIX = '_checkbox';
const Checkbox = /*#__PURE__*/forwardRef((props, ref) => {
  const inputRef = useRef(null);
  const context = useContext(CheckboxGroupContext);
  const {
    onGroupChange
  } = useContext(CheckboxGroupContext);
  const componentProps = {
    ...props
  };
  if (context.isCheckboxGroup) {
    componentProps.checked = context.checkboxGroupValue.indexOf(props.value) !== -1;
    componentProps.name = 'name' in props ? props.name : context.name;
    componentProps.disabled = 'disabled' in props ? props.disabled : context.disabled;
  }
  const {
    label,
    disabled,
    children,
    theme,
    value,
    validationStatus,
    variant = 'default',
    size = 'md',
    hint,
    dataTestId,
    onClick,
    onChange,
    name,
    checked,
    defaultChecked,
    checkIcon,
    idSuffix = ID_SUFFIX,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const [_value, setValue] = useUncontrolled({
    value: componentProps.checked,
    defaultValue: defaultChecked,
    fallbackValue: false,
    onChange
  });
  const variantClassnames = clsx({
    [styles.default]: variant === 'default',
    [styles.boxed]: variant === 'boxed',
    [styles.button]: variant === 'button'
  });
  const statusClassnames = clsx({
    [styles.error]: validationStatus === 'error',
    [styles.success]: validationStatus === 'success',
    [styles.warning]: validationStatus === 'warning'
  });
  const sizeClassnames = clsx({
    [styles.sm]: size === 'sm',
    [styles.md]: size === 'md',
    [styles.lg]: size === 'lg'
  });
  const checkboxClassNames = clsx(styles.root, variantClassnames, statusClassnames, {
    [styles.disabled]: !!disabled,
    [styles.checked]: _value || checked
  });
  const handleOnChange = useCallback(e => {
    e.persist();
    e.stopPropagation();
    if (context.isCheckboxGroup) {
      onGroupChange && onGroupChange(e);
    } else {
      setValue && setValue(e.target.checked, e);
    }
  }, [context.isCheckboxGroup, onGroupChange, setValue]);
  const onLabelClick = React.useCallback(e => {
    if (isFunction(children)) {
      e.preventDefault();
      inputRef.current && inputRef.current.click();
    }
    onClick && onClick(e);
  }, [children, onClick]);
  const hintMessage = typeof hint === 'string' ? /*#__PURE__*/jsx(Typography, {
    variant: "caption",
    tag: "span",
    theme: {
      root: styles.hint
    },
    children: hint
  }) : hint;
  const checkIconComponent = checkIcon || /*#__PURE__*/jsx(Check, {
    className: clsx(styles.checked_icon)
  });
  return /*#__PURE__*/jsxs("label", {
    htmlFor: name + idSuffix,
    ref: ref,
    ...omit(rest, ['onChange, name', 'theme']),
    className: checkboxClassNames,
    onClick: onLabelClick,
    "data-test-id": dataTestId,
    children: [/*#__PURE__*/jsx("input", {
      className: clsx(styles.hidden, styles.input, sizeClassnames),
      ref: inputRef,
      id: name + idSuffix,
      name: name,
      value: value,
      disabled: !!disabled,
      checked: _value,
      onChange: handleOnChange,
      onClick: e => e.stopPropagation(),
      type: "checkbox"
    }), isFunction(children) ? children({
      checked: _value,
      disabled,
      size,
      variant,
      value,
      onClick,
      onChange,
      validationStatus,
      name
    }) : /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx("span", {
        className: clsx(styles.box, sizeClassnames, _value && styles.checked, disabled && styles.disabled, !disabled && styles.hovered),
        children: _value && checkIconComponent
      }), (label || hint) && /*#__PURE__*/jsxs("span", {
        className: clsx(styles.content),
        children: [label && /*#__PURE__*/jsx("span", {
          className: clsx(styles.label),
          children: label
        }), hint && hintMessage]
      }), children && /*#__PURE__*/jsx("span", {
        className: clsx(styles.prefix),
        children: children
      })]
    })]
  });
});
Checkbox.displayName = 'Checkbox';

export { Checkbox };
